import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiDownload } from 'react-icons/fi';
import { leaseAPI, propertyAPI, tenantAPI, pdfAPI, propertyUnitAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const LeaseListPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ 
    property_id: '', unit_id: '', tenant_id: '', start_date: '', end_date: '', 
    monthly_rent: '', agency_fee: '0', deposit_amount: '0', payment_day: '1', terms: '' 
  });
  const navigate = useNavigate();

  useEffect(() => {
    leaseAPI.list().then(r => { setContracts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const openCreate = async () => {
    try {
      const [pRes, tRes] = await Promise.all([propertyAPI.list({ limit: 100 }), tenantAPI.list({ limit: 100 })]);
      setProperties(pRes.data.data || []);
      setTenants(tRes.data || []);
      // Pré-remplir depuis les params URL (?property=X&unit=Y)
      const urlPropertyId = searchParams.get('property');
      const urlUnitId = searchParams.get('unit');
      if (urlPropertyId) {
        const prop = (pRes.data.data || []).find(p => String(p.id) === String(urlPropertyId));
        setSelectedProperty(prop || null);
        const newForm = { ...form, property_id: urlPropertyId };
        if (urlUnitId) newForm.unit_id = urlUnitId;
        setForm(newForm);
        if (prop?.is_multi_unit) {
          setLoadingUnits(true);
          propertyUnitAPI.listByProperty(urlPropertyId)
            .then(r => setUnits(r.data || []))
            .catch(() => setUnits([]))
            .finally(() => setLoadingUnits(false));
        }
      }
    } catch (e) {}
    setShowModal(true);
  };

  // Quand la propriété change dans le formulaire
  const handlePropertyChange = async (propertyId) => {
    const prop = properties.find(p => String(p.id) === String(propertyId));
    setSelectedProperty(prop || null);
    setForm(f => ({ ...f, property_id: propertyId, unit_id: '', monthly_rent: '' }));
    setUnits([]);
    if (prop?.is_multi_unit && propertyId) {
      setLoadingUnits(true);
      try {
        const r = await propertyUnitAPI.listByProperty(propertyId);
        const available = (r.data || []).filter(u => (u.computed_status || u.status) !== 'occupied');
        setUnits(available);
      } catch (e) {
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    }
  };

  // Quand une unité est sélectionnée, pré-remplir le loyer et frais d'agence
  const handleUnitChange = (unitId) => {
    const unit = units.find(u => String(u.id) === String(unitId));
    setForm(f => ({
      ...f,
      unit_id: unitId,
      monthly_rent: unit?.monthly_rent || f.monthly_rent,
      agency_fee:   unit?.agency_fee != null ? String(unit.agency_fee) : (unit?.charges != null ? String(unit.charges) : f.agency_fee),
      deposit_amount: unit?.deposit_amount != null ? String(unit.deposit_amount) : f.deposit_amount,
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthly_rent:   parseFloat(form.monthly_rent),
        agency_fee:     parseFloat(form.agency_fee || 0),
        deposit_amount: parseFloat(form.deposit_amount || 0),
        payment_day:    parseInt(form.payment_day || 1),
        terms:          form.terms || null,
      };
      if (!payload.unit_id) delete payload.unit_id;
      const { data } = await leaseAPI.create(payload);
      setShowModal(false);
      navigate(`/leases/${data.id}`);
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la création du contrat');
    }
  };

  const downloadPDF = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await pdfAPI.downloadContract(id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = `contrat-${id}.pdf`; a.click();
    } catch (err) { alert('Erreur PDF'); }
  };

  const columns = [
    { header: 'N° Contrat', accessor: 'contract_number' },
    { header: 'Bien', render: (r) => r.property_title },
    { header: 'Locataire', render: (r) => `${r.tenant_first_name} ${r.tenant_last_name}` },
    { header: 'Loyer', render: (r) => `${parseInt(r.monthly_rent).toLocaleString()} XOF` },
    { header: 'Début', render: (r) => new Date(r.start_date).toLocaleDateString('fr-FR') },
    { header: 'Statut', render: (r) => <span className={`badge badge-${r.status === 'active' ? 'success' : r.status === 'terminated' ? 'danger' : 'warning'}`}>{r.status}</span> },
    { header: 'PDF', render: (r) => <button className="btn btn-sm btn-secondary" onClick={(e) => downloadPDF(r.id, e)}><FiDownload /></button>, width: '60px' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Contrats de bail</h1>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Nouveau contrat</button>
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="card">
          <DataTable columns={columns} data={contracts} onRowClick={(r) => navigate(`/leases/${r.id}`)} emptyMessage="Aucun contrat" />
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouveau contrat de bail" size="lg">
        <form onSubmit={handleCreate}>
          <div className="grid-2">
            <div className="form-group">
              <label>Bien *</label>
              <select
                className="form-control"
                value={form.property_id}
                onChange={e => handlePropertyChange(e.target.value)}
                required
              >
                <option value="">Sélectionner</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.title}{p.is_multi_unit ? ' 🏢 (multi-unités)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Locataire *</label>
              <select className="form-control" value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })} required>
                <option value="">Sélectionner</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
              </select>
            </div>
          </div>

          {/* Sélection d'unité si le bien est multi-unités */}
          {selectedProperty?.is_multi_unit && (
            <div className="form-group" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px' }}>
              <label style={{ color: '#1d4ed8', fontWeight: 600 }}>
                🏢 Unité / Appartement *
              </label>
              {loadingUnits ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', padding: '8px 0', fontSize: 14 }}>
                  <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Chargement des unités...
                </div>
              ) : units.length === 0 ? (
                <div style={{ color: '#ef4444', fontSize: 13, padding: '6px 0' }}>
                  Aucune unité disponible pour ce bien. Vérifiez l'onglet "Unités" du bien.
                </div>
              ) : (
                <select
                  className="form-control"
                  value={form.unit_id}
                  onChange={e => handleUnitChange(e.target.value)}
                  required
                >
                  <option value="">Sélectionner une unité</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.unit_number} — {u.unit_type === 'apartment' ? 'Appt' : u.unit_type}{u.surface_area ? ` · ${u.surface_area}m²` : ''} · {parseInt(u.monthly_rent || 0).toLocaleString()} XOF
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div className="grid-2">
            <div className="form-group"><label>Date début *</label><input type="date" className="form-control" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required /></div>
            <div className="form-group"><label>Date fin</label><input type="date" className="form-control" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div className="grid-3">
            <div className="form-group"><label>Loyer mensuel *</label><input type="number" className="form-control" value={form.monthly_rent} onChange={e => setForm({ ...form, monthly_rent: e.target.value })} required /></div>
            <div className="form-group"><label>Frais d'agence (XOF)</label><input type="number" className="form-control" placeholder="ex: 50000" value={form.agency_fee} onChange={e => setForm({ ...form, agency_fee: e.target.value })} /></div>
            <div className="form-group"><label>Depot de Garantie</label><input type="number" className="form-control" value={form.deposit_amount} onChange={e => setForm({ ...form, deposit_amount: e.target.value })} /></div>
          </div>
          <div className="form-group">
            <label>Termes, Conditions & Clauses particulières du bail</label>
            <textarea 
              className="form-control" 
              rows={3}
              placeholder="Ex: Usage d'habitation exclusive, interdiction de sous-location sans accord écrit du bailleur, entretien intérieur..."
              value={form.terms} 
              onChange={e => setForm({ ...form, terms: e.target.value })} 
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              Ces clauses figureront explicitement dans le contrat officiel de bail généré au format PDF.
            </small>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">Créer le contrat</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LeaseListPage;
